import {
  Box,
  PARTNER_ONBOARDING_ENTITY_TYPES,
  PartnerOnboardingEntityRequest,
  PartnerOnboardingEntityType,
  Skeleton,
  SubPartnerAccount,
  withStyles,
} from 'vopay';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BodyText, Card, Title } from 'UI';
import useFetchOnboardingEntities from '@/hooks/useApi/useFetchOnboardingEntities';
import useFetchPartnerPartners from '@/hooks/useApi/useFetchPartnerPartners';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useToast } from '@/providers/Toast.provider';
import ApplicationDetailsDrawer from './ApplicationDetailsDrawer/ApplicationDetailsDrawer';
import OnboardingFilters from './OnboardingFilters/OnboardingFilters';
import OnboardingSummary from './OnboardingSummary/OnboardingSummary';
import OnboardingTable from './OnboardingTable/OnboardingTable';
import getOnboardingPipeline from '@/components/Entities/Accounts/AccountsOverview/onboardingPipeline';
import AccountDetailsDrawer from '@/components/Entities/Accounts/AccountDetailsDrawer/AccountDetailsDrawer';
import * as accountPageRoute from '@/components/Entities/Accounts/accountPage.route';
import PartnerDetailsDrawer from '@/components/Partners/PartnerManagement/PartnerDetailsDrawer/PartnerDetailsDrawer';
import * as partnerPageRoute from '@/components/Partners/PartnerManagement/partnerPage.route';
import getVisibleOnboardingStages from './getVisibleOnboardingStages';
import { OnboardingFilterValues, isPartnerOnboardingStatus } from './types';
import styles from './OnboardingTrackerPage.module.scss';

const cn = withStyles(styles);

// Judgement call (no spec names a page size): 20, as the app's other listings; the backend default is 50.
const PAGE_SIZE = 20;

function isPartnerOnboardingEntityType(value: string | null): value is PartnerOnboardingEntityType {
  return PARTNER_ONBOARDING_ENTITY_TYPES.some((entityType) => entityType === value);
}

/**
 * Judgement call (specs silent): the Accounts page tiles count sub-accounts as well but link with
 * `accountType=Account`, so that value reads as both types and the rows match the tile's figure.
 */
function readEntityTypes(accountType: string | null): PartnerOnboardingEntityType[] {
  if (accountType === 'Account') return ['Account', 'SubAccount'];

  return isPartnerOnboardingEntityType(accountType) ? [accountType] : [];
}

/**
 * The Accounts page tiles (ID-13255) link here with `?stage=<raw status>&accountType=Account`, the Partners
 * page tiles (ID-13391) with `accountType=Partner`. Both are arbitrary URL text, so they are narrowed, not
 * asserted: an unknown value, or a status outside the onboarding set (the backend rejects those), reads as
 * "no filter". The retired `Underwriting` maps to `Underwriting Review`, as the backend reads it.
 */
function readFiltersFromURL(search: string): OnboardingFilterValues {
  const params = new URLSearchParams(search);
  const rawStage = params.get('stage');
  const stage = rawStage === 'Underwriting' ? 'Underwriting Review' : rawStage;
  const accountType = params.get('accountType');

  return {
    entityTypes: readEntityTypes(accountType),
    status: isPartnerOnboardingStatus(stage) ? stage : '',
    keyword: '',
  };
}

export default function OnboardingTrackerPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'OnboardingTracker.OnboardingTrackerPage' });
  const { search } = useLocation();
  const history = useHistory();
  const toast = useToast();
  const { permissions } = usePermissions();

  const {
    response: { partnerTypeLabel, isRoleAdmin },
  } = usePartnerData();

  // Partner Details shows a child partner's API key and secret, so it opens only for the users the
  // Partner Management routes admit; Viewers see partner names as plain text.
  const canOpenPartnerDetails = isRoleAdmin && permissions.PartnerCreationEnabled;

  const { canSendOnboardingEmail } = useModuleFeatureFlag('accounts');
  const { fetchingOnboardingEntities, onboardingEntities, fetchOnboardingEntities } = useFetchOnboardingEntities();
  const { fetchPartnerPartners } = useFetchPartnerPartners();

  // Seeded once from the URL; from then on the filters are page state (Search doesn't rewrite the URL).
  const [filters, setFilters] = useState<OnboardingFilterValues>(() => readFiltersFromURL(search));
  const [pageIndex, setPageIndex] = useState(0);
  // Track Progress opens Application Details; a name opens the entity's own details drawer (ID-14136).
  const [selectedEntity, setSelectedEntity] = useState<PartnerOnboardingEntityRequest | null>(null);
  const [selectedAccountID, setSelectedAccountID] = useState<string | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<SubPartnerAccount | null>(null);
  const [isLookingUpPartner, setIsLookingUpPartner] = useState(false);

  // Every pipeline stage is an onboarding status today; the guard narrows the type without a cast.
  const pipelineStages = useMemo(
    () => getOnboardingPipeline(partnerTypeLabel, permissions.partnerLiability).filter(isPartnerOnboardingStatus),
    [partnerTypeLabel, permissions.partnerLiability],
  );

  // The summary's stages (ID-14136). A status a tile link applied that the summary doesn't show (an empty
  // stage outside this partner's pipeline) is kept at the end, so the filter shows what it filters on.
  const stageCounts = onboardingEntities?.StageCounts;

  const statusOptions = useMemo(() => {
    const stages = getVisibleOnboardingStages(pipelineStages, stageCounts);

    return filters.status && !stages.includes(filters.status) ? [...stages, filters.status] : stages;
  }, [pipelineStages, stageCounts, filters.status]);

  // Numbers each partner lookup, so a slow one can't open its drawer over a later click.
  const latestPartnerLookupIDRef = useRef(0);

  // Pairs with the hook's own guard: the hook keeps a superseded response out of state, and this keeps a
  // superseded failure from toasting over the latest result.
  const latestLoadIDRef = useRef(0);

  const pageCount = onboardingEntities ? Math.ceil(onboardingEntities.PaginationMetaData.TotalRecords / PAGE_SIZE) : 0;

  useEffect(() => {
    loadEntities();
  }, [filters, pageIndex]);

  async function loadEntities() {
    latestLoadIDRef.current += 1;
    const loadID = latestLoadIDRef.current;

    try {
      await fetchOnboardingEntities({
        EntityType: filters.entityTypes,
        Status: filters.status || undefined,
        Keyword: filters.keyword || undefined,
        Limit: PAGE_SIZE,
        Offset: pageIndex * PAGE_SIZE,
      });
    } catch (error) {
      if (loadID !== latestLoadIDRef.current) return;

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('loadError', { defaultValue: 'Could not load the onboarding list: {{errorMessage}}', errorMessage }),
      );
    }
  }

  function applyFilters(values: OnboardingFilterValues) {
    setPageIndex(0);
    setFilters(values);
  }

  /**
   * An entity's name (or a row's parent) opens that entity's own details, as the Accounts and Partner
   * Accounts lists do (ID-14136): Account Details for an account or sub-account, from its ID alone.
   */
  function openEntityDetails({ EntityType, EntityID }: PartnerOnboardingEntityRequest) {
    if (EntityType === 'Partner') {
      openPartnerDetails(EntityID);
    } else {
      setSelectedAccountID(EntityID);
    }
  }

  /**
   * Partner Details needs the partner's list row (name, status, currency), which the tracker's rows don't
   * carry, so it is looked up first — as the partner page does. The drawer opens on placeholders meanwhile.
   */
  async function openPartnerDetails(partnerAccountID: string) {
    latestPartnerLookupIDRef.current += 1;
    const lookupID = latestPartnerLookupIDRef.current;

    setSelectedPartner(null);
    setIsLookingUpPartner(true);

    try {
      const { Partners } = await fetchPartnerPartners({ SearchText: partnerAccountID });
      if (lookupID !== latestPartnerLookupIDRef.current) return;

      const partner = Partners.find((candidate) => candidate.PartnerAccountID === partnerAccountID);
      if (!partner) throw new Error(`Partner account ${partnerAccountID} was not in the partner list`);

      setSelectedPartner(partner);
    } catch (error) {
      if (lookupID !== latestPartnerLookupIDRef.current) return;

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('partnerLoadError', { defaultValue: 'Could not load the partner details: {{errorMessage}}', errorMessage }),
      );
    } finally {
      if (lookupID === latestPartnerLookupIDRef.current) setIsLookingUpPartner(false);
    }
  }

  function closePartnerDetails() {
    // A lookup still in flight must not reopen the drawer the user just closed.
    latestPartnerLookupIDRef.current += 1;
    setIsLookingUpPartner(false);
    setSelectedPartner(null);
  }

  return (
    <Box flx="col" gapSize="24px">
      <div className={cn('header')}>
        <Title>{t('title', { defaultValue: 'Onboarding Tracker' })}</Title>

        <BodyText className={cn('subheading')}>
          {t('subheading', { defaultValue: 'Everything currently onboarding — partners, accounts, and sub-accounts.' })}
        </BodyText>
      </div>

      {renderSummary()}

      {/* Figma 106:23276: the filter bar sits inside the table's card, as on the Accounts page. */}
      <Card variant="section" className={cn('tableCard')}>
        <OnboardingFilters values={filters} statuses={statusOptions} onSubmit={applyFilters} />

        <OnboardingTable
          entities={onboardingEntities?.Entities ?? []}
          isLoading={fetchingOnboardingEntities}
          pageIndex={pageIndex}
          pageCount={pageCount}
          onPageChange={setPageIndex}
          onOpenEntityDetails={openEntityDetails}
          canOpenPartnerDetails={canOpenPartnerDetails}
          onTrackProgress={setSelectedEntity}
        />
      </Card>

      <ApplicationDetailsDrawer
        entity={selectedEntity}
        onClose={() => setSelectedEntity(null)}
        onChanged={loadEntities}
        isReadOnly={!isRoleAdmin}
        canResendEmail={canSendOnboardingEmail}
      />

      <AccountDetailsDrawer
        accountID={selectedAccountID}
        onClose={() => setSelectedAccountID(null)}
        onViewFullDetails={(account) =>
          history.push(accountPageRoute.getPath({ accountID: account.AccountID, currency: account.Currency }))
        }
      />

      <PartnerDetailsDrawer
        partner={selectedPartner}
        isLoadingPartner={isLookingUpPartner}
        onClose={closePartnerDetails}
        onViewFullDetails={(partner) =>
          history.push(partnerPageRoute.getPath({ partnerAccountID: partner.PartnerAccountID }))
        }
      />
    </Box>
  );

  function renderSummary() {
    // The counts cover the whole book, so a filter or page refetch keeps the last good summary on screen.
    if (onboardingEntities) {
      return (
        <OnboardingSummary
          stageCounts={onboardingEntities.StageCounts}
          totalEntities={onboardingEntities.TotalEntities}
          pipelineStages={pipelineStages}
          // Judgement call (spec and Figma silent): a pill filters the table to its stage, as the
          // Accounts page tiles do when they link here.
          onStageSelect={(status) => applyFilters({ ...filters, status })}
        />
      );
    }

    return fetchingOnboardingEntities ? <Skeleton height="178px" /> : null;
  }
}
